import { ChevronRight } from '@/components/icons/chevron-right';
import { PrescriptionPagePillIcon } from '@/components/icons/prescription-page-pill-icon';
import Link from '@/components/ui/link';
import { Routes } from '@/config/routes';
import { isPrescriptionAnalysisEnabled } from '@/lib/constants/prescription-analysis';
import cn from 'classnames';

type ExpressBuyWithPrescriptionCtaProps = {
  /** `header` = desktop header pill; `card` = homepage mobile banner */
  variant: 'header' | 'card';
  className?: string;
};

function NovoBadge({ className }: Readonly<{ className?: string }>) {
  return (
    <span
      className={cn(
        'pointer-events-none absolute z-20 bg-[#FFC83D] text-[10px] font-bold lowercase leading-none text-[#1B2A5C]',
        className,
      )}
    >
      novo
    </span>
  );
}

/**
 * Entry point to /compra-com-receita. Gated by
 * NEXT_PUBLIC_PRESCRIPTION_ANALYSIS_ENABLED.
 */
export function ExpressBuyWithPrescriptionCta({
  variant,
  className,
}: Readonly<ExpressBuyWithPrescriptionCtaProps>) {
  if (!isPrescriptionAnalysisEnabled) {
    return null;
  }

  if (variant === 'header') {
    return (
      <Link
        href={Routes.compraComReceita}
        aria-label="Compra expressa com receita — envie sua receita e compre mais rápido"
        className={cn(
          'express-buy-cta express-buy-cta--header relative hidden shrink-0 items-center gap-2.5 rounded-full px-3.5 py-2 text-white no-underline outline-none transition-transform duration-200 hover:scale-[1.02] focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-color-primary lg:inline-flex',
          className,
        )}
      >
        <NovoBadge className="-right-1 -top-1.5 rounded-full px-2 py-0.5 shadow-sm" />
        <span className="relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full">
          <PrescriptionPagePillIcon className="h-7 w-7 text-white" />
        </span>
        <span className="relative z-10 flex flex-col text-left leading-tight">
          <span className="text-[13px] font-semibold tracking-tight">
            Compra expressa
          </span>
          <span className="text-[12px] font-medium text-white/90">
            com receita
          </span>
        </span>
      </Link>
    );
  }

  return (
    <div className={cn('px-4 pb-1 pt-3 lg:hidden', className)}>
      <Link
        href={Routes.compraComReceita}
        aria-label="Compra expressa com receita — envie sua receita e compre mais rápido"
        className="express-buy-cta express-buy-cta--card relative flex w-full items-center gap-3 rounded-[20px] bg-white px-3.5 py-3.5 text-left no-underline shadow-[0_4px_16px_rgba(34,63,153,0.08)] outline-none transition-transform duration-200 hover:scale-[1.01] focus-visible:ring-2 focus-visible:ring-color-primary focus-visible:ring-offset-2"
      >
        {/*
          Mockup: yellow corner notch — flush with top+right of the card,
          top-right radius matches the card, bottom-left scoops into white.
        */}
        <NovoBadge className="right-0 top-0 rounded-bl-[14px] rounded-tr-[20px] px-2.5 py-[5px] text-[11px] tracking-wide" />
        <span className="relative z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-color-primary text-white shadow-sm">
          <PrescriptionPagePillIcon className="h-6 w-6" />
        </span>
        <span className="relative z-10 min-w-0 flex-1">
          <span className="block text-pretty text-[15px] font-bold leading-snug text-dark-blue">
            Compra expressa com receita
          </span>
          <span className="mt-0.5 block text-[13px] leading-snug text-gray-600">
            Envie sua receita e compre mais rápido
          </span>
        </span>
        <ChevronRight
          className="relative z-10 h-5 w-5 shrink-0 text-dark-blue"
          aria-hidden="true"
        />
      </Link>
    </div>
  );
}
